import type { StoryData } from "../../types/ServerCustomContext";
import { isSponsoredStory } from "../sponsored";

/** Legacy `content_type` buckets that drive the per-page slot set. */
export type SdkContentKind =
  "index" | "listing" | "article" | "internal search" | "default";

/**
 * Mirror of the design-system `PageTypesData` table (constants.ts) that the
 * legacy Gam.ts data layer used to derive `content_type` and `ad_section`
 * per route `pageType`.
 */
const PAGE_TYPE_DATA: Record<
  string,
  { contentKind: SdkContentKind; adSection: string }
> = {
  homepage: { contentKind: "index", adSection: "homepage" },
  category: { contentKind: "listing", adSection: "category" },
  article: { contentKind: "article", adSection: "article" },
  search: { contentKind: "internal search", adSection: "search" },
  videos: { contentKind: "listing", adSection: "videos" },
  tag: { contentKind: "listing", adSection: "tag" },
  latest: { contentKind: "listing", adSection: "latest-stories" },
  contenthub: { contentKind: "listing", adSection: "topics" },
  author: { contentKind: "internal search", adSection: "author" },
  techshowportal: { contentKind: "listing", adSection: "techshows" },
};

/**
 * Per-content-kind slot sets — parity with `contenttypeads` in the legacy
 * gam.js `get_adunits()`. Overlay formats (catfish/prestitial/postitial) are
 * mounted by the shell, not placed into layout placeholders.
 */
const CONTENT_KIND_SLOTS: Record<SdkContentKind, string[]> = {
  default: ["lb1"],
  index: ["lb1", "lb2", "imu1", "imu2", "imu3", "imu4"],
  listing: ["lb1", "lb2", "imu1", "imu2", "imu3", "imu4"],
  article: [
    "lb1",
    "imu1",
    "imu2",
    "imu3",
    "midarticlespecial",
    "midarticlespecial2",
    "lb2",
  ],
  "internal search": ["lb1", "lb2", "imu1", "imu2"],
};

export interface PageAdsContext {
  pageType: string;
  contentKind: SdkContentKind;
  /** GAM path tail — the legacy `ad_section` (`/5908/<pub>/<unit>/<adSection>`). */
  adSection: string;
  slots: string[];
  /** Canonical article path used for page-specific Grapeshot targeting. */
  storyPath?: string;
  articleId?: string;
  articleTags?: string[];
}

function extractArticleTags(tags?: readonly unknown[]): string[] | undefined {
  const articleTags = tags?.flatMap((tag) => {
    if (!tag || typeof tag !== "object") return [];
    const name = (tag as { name?: unknown }).name;
    return typeof name === "string" && name.trim() ? [name] : [];
  });
  return articleTags?.length ? articleTags : undefined;
}

/**
 * Derive the SDK page context from the props every route already passes to
 * `AdSettings`. Replicates the legacy Gam.ts `ad_section` derivation and the
 * gam.js featured/sponsored slot-removal rules.
 */
export function buildPageAdsContext(
  pageType: string,
  currentLocation: { pathname: string },
  storyData?: Partial<StoryData>,
): PageAdsContext {
  const mapped = PAGE_TYPE_DATA[pageType];
  const contentKind: SdkContentKind = mapped?.contentKind ?? "default";
  const fallbackAdSection = (mapped?.adSection ?? pageType) || "default";

  let adSection = fallbackAdSection;
  if (pageType === "category") {
    adSection =
      currentLocation.pathname.replace(/^\/+/, "").replace(/\/+$/, "") ||
      fallbackAdSection;
  }
  if (pageType === "article") {
    adSection =
      (storyData?.sections?.[0]?.path ?? "")
        .split("/")
        .filter(Boolean)
        .join("/") || fallbackAdSection;
  }

  let slots = [...CONTENT_KIND_SLOTS[contentKind]];

  if (pageType === "article") {
    // Featured articles drop the leaderboard and lower sidebar IMUs.
    if (storyData?.typeName === "FeatureStory") {
      slots = slots.filter((s) => !["lb1", "imu2", "imu3"].includes(s));
    }
    // Active sponsorship windows drop the inline article slots.
    if (isSponsoredStory(storyData?.sponsorship, storyData?.typeName)) {
      slots = slots.filter(
        (s) => !["imu1", "midarticlespecial", "midarticlespecial2"].includes(s),
      );
    }
  }

  return {
    pageType,
    contentKind,
    adSection,
    slots,
    storyPath: storyData?.path || undefined,
    articleId: storyData?.id || undefined,
    articleTags: extractArticleTags(storyData?.tags),
  };
}
